import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUser } from './UserContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';

interface SocketContextValue {
  /** Singleton socket connected to the `/sessions` namespace. Becomes non-null
   *  the moment the io() call returns, BEFORE the 'connect' handshake completes
   *  — that way consumers attach their listeners early enough to catch the
   *  snapshot of active sessions the backend emits at handshake time. */
  socket: Socket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketContextValue>({ socket: null, connected: false });

/**
 * Provides a single shared `socket.io-client` connection to the backend's
 * `/sessions` namespace. The connection is opened only when the user is
 * authenticated (a JWT is in localStorage); we tear it down on logout so the
 * backend isn't holding zombie rooms.
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      setSocket((current) => {
        if (current) current.disconnect();
        return null;
      });
      setConnected(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const s = io(`${API_BASE_URL}/sessions`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 800
    });

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', () => setConnected(false));

    // Expose the socket synchronously — consumers can subscribe before the
    // handshake completes so the backend's on-connect snapshot is not lost.
    setSocket(s);

    return () => {
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [user]);

  const value = useMemo<SocketContextValue>(() => ({ socket, connected }), [socket, connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}
